import { html, PropertyValueMap, unsafeCSS } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'

import { DdsElement } from '@/internal/dds-hu-element'
import { SizedMixin } from '@/internal/mixin/sizedMixin'
import { t } from '@/localization/localization'

import DapDSFileInput from './file-input.component'
import styles from './file-input-list.scss?inline'
import DapDSFileInputListItem from './file-input-list-item.component'

/**
 * `dap-ds-file-input-list`
 * @summary A file input list is a container for file input items.
 * @element dap-ds-file-input-list
 * @title - File input list
 * @group file-input
 *
 * @csspart base - The main file input list container.
 *
 * @cssproperty --dds-file-list-display - Controls the display property (default: flex)
 * @cssproperty --dds-file-list-flex-direction - Controls the flex-direction property (default: column)
 * @cssproperty --dds-file-list-padding - Controls the padding property (default: 0)
 * @cssproperty --dds-file-list-list-style - Controls the list-style-type property (default: none)
 * @cssproperty --dds-file-list-gap - Controls the row-gap property (default: var(--dds-spacing-200))
 */
@customElement('dap-ds-file-input-list')
export default class DapDSFileInputList extends SizedMixin(DdsElement) {
  /** The id of the file input element to bind to. */
  @property({ type: String }) for: string

  /** Disables the file input list. */
  @property({ type: Boolean, reflect: true }) disabled = false

  /** Whether to show the delete button in the file input list item. */
  @property({ type: String }) showDeleteButton = 'true'

  @state()
  private _files: DapDSFileInputListItem[] = []

  static readonly styles = unsafeCSS(styles)

  private fileInputElement: DapDSFileInput | null = null

  get files() {
    return this.querySelectorAll('dap-ds-file-input-list-item')
  }

  getFileByName(name: string) {
    return this.shadowRoot!.querySelector(
      `dap-ds-file-input-list-item[filename="${name}"]`,
    )
  }

  private _handleFileChange(event: Event) {
    const fileComponents = (event as CustomEvent).detail.files.map(
      (file: File) =>
        html`<dap-ds-file-input-list-item
          size=${this.size}
          .fileData=${file}
          fileName=${file.name}
          showDeleteButton=${this.showDeleteButton}
          .disabled=${this.disabled}></dap-ds-file-input-list-item>`,
    )

    this._files = fileComponents
  }

  protected firstUpdated(
    _changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    super.firstUpdated(_changedProperties)
    if (this.for) {
      this.fileInputElement = document.querySelector(`#${this.for}`)
      if (this.fileInputElement) {
        this.fileInputElement.fileList = this
        this.fileInputElement?.addEventListener(
          'dds-file-change',
          this._handleFileChange.bind(this),
        )
      }
    }
  }

  removeFile(event: CustomEvent) {
    this.fileInputElement?.dispatchEvent(
      new CustomEvent('dds-file-remove', {
        bubbles: false,
        composed: true,
        detail: event.detail,
      }),
    )
  }

  render() {
    return html`<ul
      class="file-input-list"
      part="base"
      role="list"
      aria-label=${this.ariaLabel ?? t('fileInput.fileList')}
      @dds-file-remove=${this.removeFile}>
      ${this._files}
    </ul>`
  }
}
